import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    CropWaterUse,
    ProbeSimulation,
    WaterRisks,
} from "@/types/water";
import dayjs from "dayjs";
import { useMemo } from "react";
import {
    Label,
    ResponsiveContainer,
    YAxis,
    XAxis,
    Tooltip,
    Legend,
    Line,
    LineChart,
    ReferenceArea,
} from "recharts";
import { scaleTime } from "d3-scale";
import { useTranslation } from "react-i18next";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { DateValue, RangeValue, Skeleton } from "@heroui/react";
import useAuth from "@/lib/auth/useAuth";
import { Resource } from "@/types/user";
import SoilWaterContent from "./SoilWaterContent";
import { getTodayWithTimeTravel } from "@/lib/utils/date";

function mmToIn(mm: number) {
    return mm * 0.0393701;
}

function WaterCharts({
    dateRange,
}: {
    dateRange: RangeValue<DateValue> | null;
}) {
    const { useGetRequest } = useRequest();
    const { fieldId, fieldHasIrrigation } = useFieldDashboard() ?? {};
    const { formatDateMMDDYYYY, formatUnit, isImperial } = useI18n();
    const { t } = useTranslation();

    const today = useMemo(
        () => getTodayWithTimeTravel().toDate().getTime(),
        [],
    );

    const probeSimulationQuery = useGetRequest<ProbeSimulation>({
        url: `fields/${fieldId}/probe_simulation/`,
    });

    const probeSimulationData = useMemo(() => {
        return sortArrayByKey(
            probeSimulationQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(
                            dateRange?.start?.toString(),
                        ) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [probeSimulationQuery.data?.data, dateRange]);

    const probeSimulationColorKey = useMemo(() => {
        return {
            swc_005cm: {
                color: "#4F8DFD",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(50), 0) : 50} in`,
            },
            swc_015cm: {
                color: "#42C68E",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(150), 0) : 150} in`,
            },
            swc_030cm: {
                color: "#FFD16F",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(300), 0) : 300} in`,
            },
            swc_060cm: {
                color: "#F96C77",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(600), 0) : 600} in`,
            },
        };
    }, [isImperial]);

    const cropWaterUseQuery = useGetRequest<CropWaterUse>({
        url: `fields/${fieldId}/crop_water_use/`,
    });

    const cropWaterUseData = useMemo(() => {
        return sortArrayByKey(
            cropWaterUseQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(
                            dateRange?.start?.toString(),
                        ) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    range: [d.et_under, d.et_over],
                    replacement: isImperial ? 0.3 : 7.62,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [cropWaterUseQuery.data?.data, isImperial, dateRange]);

    const { isAtLeastDetail } = useAuth();

    const waterRisksQuery = useGetRequest<WaterRisks>({
        url: `fields/${fieldId}/water_risks/`,
        enabled: isAtLeastDetail(Resource.IRRIGATION),
    });

    const waterRisksData = useMemo(() => {
        return sortArrayByKey(
            waterRisksQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(
                            dateRange?.start?.toString(),
                        ) &&
                        dayjs(d.time).isSameOrBefore(dateRange?.end?.toString()),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [waterRisksQuery.data?.data, dateRange]);

    const allTimes = [
        ...probeSimulationData.map((d) => d.time as number),
        ...cropWaterUseData.map((d) => d.time as number),
        ...waterRisksData.map((d) => d.time as number),
    ].filter((t) => Number.isFinite(t));
    const maxValue = allTimes.length ? Math.max(...allTimes) : today;
    const minValue = allTimes.length ? Math.min(...allTimes) : today;
    const timeScale = scaleTime().domain([minValue, maxValue]);
    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const forecastReferenceArea =
        domain[1] > today ? (
            <>
                <ReferenceArea
                    x1={today}
                    x2={domain[1]}
                    stroke="#09426A"
                    strokeOpacity={0.5}
                    fill="#09426A"
                    fillOpacity={0.05}
                >
                    <Label
                        value="Forecasted"
                        position="top"
                        fontSize={10}
                        dx={-14}
                    />
                </ReferenceArea>
            </>
        ) : null;

    const { elementRef, isResizing } = useResizeObserver(250);

    return (
        <div className="w-full h-[100rem]  " ref={elementRef}>
            {isResizing ? (
                <Skeleton className="h-full w-full" />
            ) : (
                <div className="flex flex-col gap-3">
                    {!fieldHasIrrigation ? (
                        <div>
                            <div className="text-sm italic text-center mt-4">
                                Enroll in Irrigation to view this data
                            </div>
                        </div>
                    ) : (
                        <>
                            <SoilWaterContent
                                fieldId={fieldId}
                                dateRange={dateRange}
                            />
                            <div className="font-semibold">
                                Probe Simulation
                            </div>
                            {probeSimulationData.length ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart
                                        data={probeSimulationData}
                                        syncId="water"
                                    >
                                        <XAxis
                                            dataKey="time"
                                            scale="linear"
                                            type="number"
                                            spacing={50}
                                            angle={-8}
                                            fontSize={12}
                                            tickFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            domain={domain}
                                            ticks={ticks}
                                        />
                                        <YAxis
                                            fontSize={12}
                                            label={{
                                                value: `${t(
                                                    "chartLabels.probeSimulation",
                                                    {
                                                        ns: "insights",
                                                    },
                                                )} ${formatUnit("in", "mm")}`,
                                                angle: -90,
                                                dx: -20,
                                            }}
                                        />
                                        {Object.entries(
                                            probeSimulationColorKey,
                                        ).map(([key, value]) => (
                                            <Line
                                                key={key}
                                                dataKey={key}
                                                stroke={value.color}
                                                dot={false}
                                                strokeWidth={1}
                                                name={value.name}
                                            />
                                        ))}
                                        <Tooltip
                                            labelFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            formatter={(value, name) => [
                                                `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                                name,
                                            ]}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            height={36}
                                            fontSize={12}
                                            wrapperStyle={{ fontSize: "12px" }}
                                        />
                                        {forecastReferenceArea}
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="text-sm italic text-center mt-4">
                                    No data available
                                </div>
                            )}
                            <div className="font-semibold">Crop Water Use</div>
                            {cropWaterUseData.length ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart
                                        data={cropWaterUseData}
                                        syncId="water"
                                    >
                                        <XAxis
                                            dataKey="time"
                                            scale="linear"
                                            type="number"
                                            spacing={50}
                                            angle={-8}
                                            fontSize={12}
                                            tickFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            domain={domain}
                                            ticks={ticks}
                                        />
                                        <YAxis
                                            fontSize={12}
                                            label={{
                                                value: t(
                                                    "chartLabels.cropWaterUse",
                                                    {
                                                        ns: "insights",
                                                    },
                                                ),
                                                angle: -90,
                                                dx: -20,
                                            }}
                                        />
                                        <Line
                                            dataKey="et_estimate"
                                            stroke="orange"
                                            dot={false}
                                            strokeWidth={1}
                                            name={t("chartLabels.etEstimate", {
                                                ns: "insights",
                                            })}
                                        />
                                        <Line
                                            dataKey="range"
                                            stroke="orange"
                                            dot={false}
                                            strokeWidth={1}
                                            opacity={0.5}
                                            legendType="none"
                                            tooltipType="none"
                                        />
                                        <Line
                                            dataKey="replacement"
                                            stroke="black"
                                            strokeDasharray="5 5"
                                            dot={false}
                                            strokeWidth={1}
                                            tooltipType="none"
                                            name={t("chartLabels.replacement", {
                                                ns: "insights",
                                            })}
                                        />
                                        <Tooltip
                                            labelFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            formatter={(value, name) => [
                                                `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                                name,
                                            ]}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            height={36}
                                            fontSize={12}
                                            wrapperStyle={{ fontSize: "12px" }}
                                        />
                                        {forecastReferenceArea}
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <div>
                                    <div className="text-sm italic text-center mt-4">
                                        No data available
                                    </div>
                                </div>
                            )}
                            <div className="font-semibold">Water Risks</div>
                            {isAtLeastDetail(Resource.IRRIGATION) &&
                                waterRisksData.length ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <LineChart
                                        data={waterRisksData}
                                        syncId="water"
                                    >
                                        <XAxis
                                            dataKey="time"
                                            scale="linear"
                                            type="number"
                                            spacing={50}
                                            angle={-8}
                                            fontSize={12}
                                            tickFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            domain={domain}
                                            ticks={ticks}
                                        />
                                        <YAxis
                                            fontSize={12}
                                            label={{
                                                value: "Risk %",
                                                angle: -90,
                                                dx: -20,
                                            }}
                                            domain={[0, 100]}
                                            allowDataOverflow
                                            type="number"
                                            scale="linear"
                                        />
                                        <Line
                                            dataKey="leach_risk"
                                            stroke="blue"
                                            dot={false}
                                            strokeWidth={1}
                                            name={t(
                                                "chartLabels.leachingRisk",
                                                {
                                                    ns: "insights",
                                                },
                                            )}
                                            legendType="line"
                                        />
                                        <Line
                                            dataKey="water_risk"
                                            stroke="orange"
                                            dot={false}
                                            strokeWidth={1}
                                            name={t("chartLabels.waterRisk", {
                                                ns: "insights",
                                            })}
                                            legendType="line"
                                        />
                                        <Tooltip
                                            labelFormatter={(t) =>
                                                formatDateMMDDYYYY(t)
                                            }
                                            formatter={(value, name) => [
                                                `${roundTo(value as number, 1)} %`,
                                                name,
                                            ]}
                                        />
                                        <Legend
                                            verticalAlign="top"
                                            height={36}
                                            fontSize={12}
                                            wrapperStyle={{ fontSize: "12px" }}
                                        />
                                        {forecastReferenceArea}
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : isAtLeastDetail(Resource.IRRIGATION) ? (
                                <div>
                                    <div className="text-sm italic text-center mt-4">
                                        No data available
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default WaterCharts;
